import React, { useEffect, useRef, useState } from 'react';
import '../../ForListPage.scss';
import './RecruitList.scss';
import { roleMarkerClass } from '../../../utils/roleMarker';
import { useNavigate, useLocation } from 'react-router-dom';
import axios from 'axios';
import MainURL from '../../../MainURL';
import { useAtomValue } from 'jotai';
import { loginStateAtom, userDataAtom } from '../../../AtomStore';
import { fetchScrapStatusMap, scrapKeyOf, toggleScrap } from '../../mypage/common/scrapApi';
import { sortList, citydata } from '../../../DefaultData';
import Pagination from '../../../components/Pagination';
import Loading from '../../../components/Loading';
import ScrollToTopButton from '../../../components/ScrollToTopButton';
import type { RecruitBoardConfig, RecruitFilterTab, RecruitListItem } from './RecruitTypes';
import { RECRUIT_RELIGIOUSBODY_OPTIONS } from './recruitConfigs';
import {
  filterRecruitItems,
  filtersToAlertDraft,
  highlightSearchText,
  PAY_FILTER_OPTIONS,
  POSITION_FILTER_OPTIONS,
  renderPreview30,
  renderPreview50,
  safeJsonParse,
  compareRecruitByDate,
} from './recruitUtils';
import type { RecruitListLocationState, RecruitReturnState } from './recruitNavigation';
import {
  clearRecruitListRestore,
  recruitReturnHasFilters,
  recruitReturnShouldRestoreScroll,
  resolveInitialRecruitReturn,
  restoreScroll,
  saveRecruitListRestore,
} from './recruitNavigation';

type PayEntry = {
  sort: string;
  paySort: string;
  selectCost: string;
  inputCost: string;
};

type PartEntry = {
  sort: string;
  content: string;
};

type ApplyTime = {
  startDay: string;
  endDay: string;
  daySort: string;
};

export default function RecruitList({ config }: { config: RecruitBoardConfig }) {
  const navigate = useNavigate();
  const location = useLocation();
  const initialRestoreRef = useRef<RecruitReturnState | null | undefined>(undefined);
  if (initialRestoreRef.current === undefined) {
    initialRestoreRef.current = resolveInitialRecruitReturn(
      location.state as RecruitListLocationState | null,
      config.listPath,
    );
  }
  const initialRestore = initialRestoreRef.current;

  const isLogin = useAtomValue(loginStateAtom);
  const userData = useAtomValue(userDataAtom);
  const [scrapMap, setScrapMap] = useState<Record<string, boolean>>({});

  const hasSortTab = config.filterTabs.includes('직무');
  const hasLocationTab = config.filterTabs.includes('지역');
  const hasReligiousbodyTab = config.filterTabs.includes('교단');
  const hasPositionTab = config.filterTabs.includes('직분');
  const hasPayTab = config.filterTabs.includes('사례비');

  const [refresh, setRefresh] = useState<boolean>(false);
  const [listSort, setListSort] = useState('크롤링');
  const [listView, setListView] = useState<RecruitListItem[]>([]);
  const [searchResult, setSearchResult] = useState<RecruitListItem[] | null>(null);
  const [currentPage, setCurrentPage] = useState<number>(initialRestore?.currentPage ?? 1);
  const [listAllLength, setListAllLength] = useState<number>(0);
  const [listAllLengthOrigin, setListAllLengthOrigin] = useState<number>(0);
  const [isLoading, setIsLoading] = useState<boolean>(false);

  const [activeTab, setActiveTab] = useState<RecruitFilterTab>(config.filterTabs[0]);
  const [searchWord, setSearchWord] = useState(initialRestore?.searchWord ?? '');
  const [selectedSort, setSelectedSort] = useState<string[]>(
    hasSortTab ? initialRestore?.selectedSort ?? [] : [],
  );
  const [selectedLocation, setSelectedLocation] = useState<string[]>(
    hasLocationTab ? initialRestore?.selectedLocation ?? [] : [],
  );
  const [selectedReligiousbody, setSelectedReligiousbody] = useState<string[]>(
    hasReligiousbodyTab ? initialRestore?.selectedReligiousbody ?? [] : [],
  );
  const [selectedPosition, setSelectedPosition] = useState<string[]>(
    hasPositionTab ? initialRestore?.selectedPosition ?? [] : [],
  );
  const [selectedPay, setSelectedPay] = useState<string[]>(
    hasPayTab ? initialRestore?.selectedPay ?? [] : [],
  );
  const [isSearching, setIsSearching] = useState(
    Boolean(
      initialRestore &&
        recruitReturnHasFilters(initialRestore, hasSortTab, hasLocationTab, hasReligiousbodyTab),
    ),
  );
  const pendingScrollYRef = useRef<number | null>(
    initialRestore && recruitReturnShouldRestoreScroll(initialRestore)
      ? (initialRestore.scrollY ?? 0)
      : null,
  );
  const hasInitializedRef = useRef(false);
  const skipInitialFetchRef = useRef(
    Boolean(
      initialRestore &&
        recruitReturnHasFilters(initialRestore, hasSortTab, hasLocationTab, hasReligiousbodyTab),
    ),
  );

  const itemsPerPage = 10;
  const totalPages = Math.ceil((searchResult ? searchResult.length : listAllLength) / itemsPerPage);
  const cityNames: string[] = citydata.map((c: { city: string }) => c.city);
  const locationOptions = config.locationOptions ?? cityNames;
  const sortOptions = config.sortOptions ?? sortList;
  const scrapTableType = config.tableType ?? config.simplePostType ?? 'minister';

  const getTabOptions = (tab: RecruitFilterTab): string[] => {
    if (tab === '직무') return sortOptions;
    if (tab === '지역') return locationOptions;
    if (tab === '교단') return RECRUIT_RELIGIOUSBODY_OPTIONS;
    if (tab === '직분') return Object.keys(POSITION_FILTER_OPTIONS);
    if (tab === '사례비') return Object.keys(PAY_FILTER_OPTIONS);
    return [];
  };

  const getCurrentSearchTerms = () => {
    const terms = [
      ...(hasSortTab ? selectedSort : []),
      ...(hasLocationTab ? selectedLocation : []),
      ...(hasReligiousbodyTab ? selectedReligiousbody : []),
      ...(hasPositionTab ? selectedPosition : []),
    ];
    if (searchWord.trim()) {
      terms.push(searchWord.trim());
    }
    return terms;
  };

  const getSelectedChips = () => [
    ...(hasSortTab ? selectedSort : []),
    ...(hasLocationTab ? selectedLocation : []),
    ...(hasReligiousbodyTab ? selectedReligiousbody : []),
    ...(hasPositionTab ? selectedPosition : []),
    ...(hasPayTab ? selectedPay : []),
  ];

  const finishRestore = () => {
    if (pendingScrollYRef.current !== null) {
      restoreScroll(pendingScrollYRef.current);
      pendingScrollYRef.current = null;
    }
    clearRecruitListRestore(config.listPath);
    navigate(config.listPath, { replace: true, state: {} });
  };

  const fetchPosts = async () => {
    if (searchResult) return;
    setIsLoading(true);
    try {
      const res = await axios.get(`${MainURL}/${config.apiBase}/getrecruitdata/${currentPage}`);
      if (res.data.resultData) {
        setListView(res.data.resultData);
        setListAllLength(res.data.totalCount);
        setListAllLengthOrigin(res.data.totalCount);
      } else {
        // 공고가 없는 게시판(resultData: false)은 빈 목록으로 표시
        setListView([]);
        setListAllLength(0);
        setListAllLengthOrigin(0);
      }
    } catch (error) {
      console.error(error);
    } finally {
      setIsLoading(false);
      if (pendingScrollYRef.current !== null) {
        finishRestore();
      }
    }
  };

  useEffect(() => {
    if (!hasInitializedRef.current) {
      hasInitializedRef.current = true;
      const restored = initialRestore;
      if (
        restored &&
        recruitReturnHasFilters(restored, hasSortTab, hasLocationTab, hasReligiousbodyTab)
      ) {
        skipInitialFetchRef.current = false;
        void performUnifiedSearch(
          {
            searchWord: restored.searchWord || '',
            selectedSort: hasSortTab ? restored.selectedSort || [] : [],
            selectedLocation: hasLocationTab ? restored.selectedLocation || [] : [],
            selectedReligiousbody: hasReligiousbodyTab ? restored.selectedReligiousbody || [] : [],
            selectedPosition: hasPositionTab ? restored.selectedPosition || [] : [],
            selectedPay: hasPayTab ? restored.selectedPay || [] : [],
          },
          restored.currentPage ?? 1,
        ).then(() => {
          if (pendingScrollYRef.current !== null) {
            finishRestore();
          } else if (restored) {
            clearRecruitListRestore(config.listPath);
            navigate(config.listPath, { replace: true, state: {} });
          }
        });
        return;
      }

      if (restored && pendingScrollYRef.current === null) {
        clearRecruitListRestore(config.listPath);
        navigate(config.listPath, { replace: true, state: {} });
      }
    }

    if (skipInitialFetchRef.current) {
      skipInitialFetchRef.current = false;
      return;
    }

    void fetchPosts();
  }, [refresh, currentPage, listSort]);

  const handleSelect = (item: string, type: RecruitFilterTab) => {
    let selected: string[];
    let setSelected: React.Dispatch<React.SetStateAction<string[]>>;

    if (type === '직무') {
      selected = selectedSort;
      setSelected = setSelectedSort;
    } else if (type === '지역') {
      selected = selectedLocation;
      setSelected = setSelectedLocation;
    } else if (type === '교단') {
      selected = selectedReligiousbody;
      setSelected = setSelectedReligiousbody;
    } else if (type === '직분') {
      selected = selectedPosition;
      setSelected = setSelectedPosition;
    } else if (type === '사례비') {
      selected = selectedPay;
      setSelected = setSelectedPay;
    } else {
      return;
    }

    const newSelected = selected.includes(item)
      ? selected.filter((i) => i !== item)
      : [...selected, item];
    setSelected(newSelected);
  };

  const handleClearSearch = () => {
    clearRecruitListRestore(config.listPath);
    setSearchResult(null);
    setCurrentPage(1);
    if (hasSortTab) setSelectedSort([]);
    if (hasLocationTab) setSelectedLocation([]);
    if (hasReligiousbodyTab) setSelectedReligiousbody([]);
    if (hasPositionTab) setSelectedPosition([]);
    if (hasPayTab) setSelectedPay([]);
    setSearchWord('');
    setIsSearching(false);
    setListAllLength(listAllLengthOrigin);
  };

  const handleUnifiedSearch = async () => {
    const noFilters =
      !searchWord &&
      (!hasSortTab || selectedSort.length === 0) &&
      (!hasLocationTab || selectedLocation.length === 0) &&
      (!hasReligiousbodyTab || selectedReligiousbody.length === 0) &&
      (!hasPositionTab || selectedPosition.length === 0) &&
      (!hasPayTab || selectedPay.length === 0);
    if (noFilters) {
      alert('검색 조건을 입력/선택해주세요');
      return;
    }

    setIsSearching(true);
    setCurrentPage(1);

    const res = await axios.post(`${MainURL}/${config.apiBase}/recruitsearchunified`, {
      searchWord: searchWord || '',
      sort: hasSortTab ? selectedSort : [],
      location: hasLocationTab ? selectedLocation : [],
      religiousbody: hasReligiousbodyTab ? selectedReligiousbody : [],
    });

    if (res.data.resultData) {
      const filtered = filterRecruitItems(
        res.data.resultData,
        hasSortTab ? selectedSort : [],
        hasLocationTab ? selectedLocation : [],
        hasReligiousbodyTab ? selectedReligiousbody : [],
        config,
        hasPositionTab ? selectedPosition : [],
        hasPayTab ? selectedPay : [],
      );
      const sorted = filtered.sort(compareRecruitByDate);
      setSearchResult(sorted);
      setListAllLength(sorted.length);
    } else {
      setSearchResult([]);
      setListAllLength(0);
    }
  };

  const performUnifiedSearch = async (
    {
      searchWord: sw,
      selectedSort: ss,
      selectedLocation: sl,
      selectedReligiousbody: sr,
      selectedPosition: sp = [],
      selectedPay: spay = [],
    }: {
      searchWord: string;
      selectedSort: string[];
      selectedLocation: string[];
      selectedReligiousbody: string[];
      selectedPosition?: string[];
      selectedPay?: string[];
    },
    targetPage = 1,
  ) => {
    setIsSearching(true);
    const res = await axios.post(`${MainURL}/${config.apiBase}/recruitsearchunified`, {
      searchWord: sw || '',
      sort: ss,
      location: sl,
      religiousbody: sr,
    });
    if (res.data.resultData) {
      const filtered = filterRecruitItems(res.data.resultData, ss, sl, sr, config, sp, spay);
      const sorted = filtered.sort(compareRecruitByDate);
      setSearchResult(sorted);
      setListAllLength(sorted.length);
      setCurrentPage(targetPage);
    } else {
      setSearchResult([]);
      setListAllLength(0);
      setCurrentPage(targetPage);
    }
  };

  const pagedList = searchResult
    ? searchResult.slice((currentPage - 1) * itemsPerPage, currentPage * itemsPerPage)
    : listView;

  useEffect(() => {
    if (!userData.userAccount || pagedList.length === 0) {
      setScrapMap({});
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const targets = pagedList.map(item => ({
          targetType: 'recruit' as const,
          targetId: String(item.id),
          tableType: scrapTableType,
        }));
        const map = await fetchScrapStatusMap(userData.userAccount, targets);
        if (!cancelled) setScrapMap(map);
      } catch {
        if (!cancelled) setScrapMap({});
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [userData.userAccount, pagedList, scrapTableType]);

  const handleToggleScrap = async (item: RecruitListItem) => {
    if (!isLogin || !userData.userAccount) {
      alert('로그인이 필요합니다.');
      return;
    }
    const tableType = scrapTableType;
    const payload = {
      targetType: 'recruit' as const,
      targetId: String(item.id),
      tableType,
      title: String(item.title || ''),
      subtitle: String(item.church || ''),
      meta: `${item.location || ''} ${item.locationDetail || ''}`.trim(),
      linkPath: config.detailPath ? `${config.detailPath}/${item.id}` : '',
    };
    const key = scrapKeyOf(payload);
    const before = Boolean(scrapMap[key]);
    setScrapMap(prev => ({ ...prev, [key]: !before }));
    try {
      const res = await toggleScrap(userData.userAccount, payload);
      setScrapMap(prev => ({ ...prev, [key]: res.scrapped }));
    } catch {
      setScrapMap(prev => ({ ...prev, [key]: before }));
      alert('스크랩 처리에 실패했습니다.');
    }
  };

  const handleRemoveSelected = (item: string) => {
    if (hasSortTab && selectedSort.includes(item)) {
      setSelectedSort(selectedSort.filter((i) => i !== item));
    } else if (hasLocationTab && selectedLocation.includes(item)) {
      setSelectedLocation(selectedLocation.filter((i) => i !== item));
    } else if (hasReligiousbodyTab && selectedReligiousbody.includes(item)) {
      setSelectedReligiousbody(selectedReligiousbody.filter((i) => i !== item));
    } else if (hasPositionTab && selectedPosition.includes(item)) {
      setSelectedPosition(selectedPosition.filter((i) => i !== item));
    } else if (hasPayTab && selectedPay.includes(item)) {
      setSelectedPay(selectedPay.filter((i) => i !== item));
    }
  };

  const handleItemClick = (itemId: string | number) => {
    const today = new Date();
    const yyyy = today.getFullYear();
    const mm = String(today.getMonth() + 1).padStart(2, '0');
    const dd = String(today.getDate()).padStart(2, '0');
    const date = `${yyyy}-${mm}-${dd}`;
    axios.post(`${MainURL}/admin/countup`, { date, type: 'recruitview' });

    const recruitState: RecruitReturnState = {
      searchWord,
      selectedSort: hasSortTab ? selectedSort : [],
      selectedLocation: hasLocationTab ? selectedLocation : [],
      selectedReligiousbody: hasReligiousbodyTab ? selectedReligiousbody : [],
      selectedPosition: hasPositionTab ? selectedPosition : [],
      selectedPay: hasPayTab ? selectedPay : [],
      currentPage,
      scrollY: window.scrollY,
    };
    saveRecruitListRestore(config.listPath, recruitState);
    window.scrollTo(0, 0);
    navigate(`${config.detailPath}/${itemId}`, { state: { recruitState } });
  };

  const renderHighlighted = (text: string) =>
    searchResult ? highlightSearchText(text, getCurrentSearchTerms()) : text;

  const renderPayLine = (payEntry: PayEntry) => {
    if (!payEntry || (!payEntry.inputCost && !payEntry.selectCost)) return null;
    if (payEntry.inputCost === '교회내규에따라' || payEntry.inputCost === '협의후결정') {
      return (
        <p
          className="recruit__pay"
          dangerouslySetInnerHTML={{ __html: renderHighlighted(renderPreview50(payEntry.inputCost)) }}
        />
      );
    }
    if (payEntry.selectCost === '' && payEntry.inputCost !== '') {
      return (
        <p
          className="recruit__pay"
          dangerouslySetInnerHTML={{
            __html: renderHighlighted(renderPreview50(`${payEntry.sort} : ${payEntry.inputCost}`)),
          }}
        />
      );
    }
    return (
      <p
        className="recruit__pay"
        dangerouslySetInnerHTML={{
          __html: renderHighlighted(
            renderPreview50(`${payEntry.sort} : ${payEntry.paySort === 'select' ? '' : payEntry.paySort} ${payEntry.selectCost}`),
          ),
        }}
      />
    );
  };

  const isTabItemSelected = (item: string, tab: RecruitFilterTab) => {
    if (tab === '직무') return selectedSort.includes(item);
    if (tab === '지역') return selectedLocation.includes(item);
    if (tab === '교단') return selectedReligiousbody.includes(item);
    if (tab === '직분') return selectedPosition.includes(item);
    if (tab === '사례비') return selectedPay.includes(item);
    return false;
  };

  return (
    <div className="recruit">
      <div className="inner">
        <div className="subpage__main">
          <div className="subpage__main__title">
            <h3 className="mm-serif">{config.boardTitle}</h3>
            <span className="subpage__main__count">
              전체 {(searchResult ? searchResult.length : listAllLength).toLocaleString('ko-KR')}건
            </span>
            <div
              className="postBtn"
              onClick={() => {
                if (isLogin) {
                  navigate(config.postPath);
                } else {
                  alert('로그인이 필요합니다.');
                }
              }}
            >
              <p>공고등록</p>
            </div>
          </div>

          <div className="subpage__main__filter">
            <div className="subpage__main__tabrow">
              {config.filterTabs.map((tab) => (
                <button
                  key={tab}
                  className={`subpage__main__tabbtn${activeTab === tab ? ' active' : ''}`}
                  onClick={() => setActiveTab(tab)}
                >
                  {tab}
                </button>
              ))}
            </div>

            <div className="checkInputCover Recruit">
              {getTabOptions(activeTab).map((item, index) => (
                <div
                  className={`checkInputbox ${isTabItemSelected(item, activeTab) ? 'selected' : ''}`}
                  key={index}
                  onClick={() => handleSelect(item, activeTab)}
                >
                  {activeTab === '교단' ? (
                    <span>
                      <img
                        src={`${MainURL}/${config.religiousbodyDetailImagePath}/${item}.jpg`}
                        alt={item}
                        onError={(e) => { (e.currentTarget as HTMLImageElement).style.display = 'none'; }}
                      />
                      <p>{item}</p>
                    </span>
                  ) : (
                    <p>{item}</p>
                  )}
                </div>
              ))}
            </div>

            {getSelectedChips().length > 0 && (
              <div className="subpage__main__selectedwords">
                <b>선택</b>
                <div className="subpage__main__selectedwords__list">
                  {getSelectedChips().map((item, idx) => (
                    <span key={idx} className="subpage__main__selectedwords__item">
                      {item}
                      <button
                        className="subpage__main__selectedwords__remove"
                        onClick={() => handleRemoveSelected(item)}
                        aria-label="삭제"
                        type="button"
                      >
                        ×
                      </button>
                    </span>
                  ))}
                </div>
              </div>
            )}

            <div className="subpage__main__search">
              <input
                className="inputdefault width"
                type="text"
                placeholder="교회명, 제목으로 검색"
                value={searchWord}
                onChange={(e) => {
                  setSearchWord(e.target.value);
                }}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    handleUnifiedSearch();
                  }
                }}
              />
              <div className="buttons">
                <div className="btn search" onClick={handleUnifiedSearch}>
                  <p>검색</p>
                </div>
                <div className="btn reset" onClick={handleClearSearch}>
                  <p>초기화</p>
                </div>
                {/* P1 공고 알림 — 지금 고른 필터로 알림 조건 저장 (국내·해외 사역지만) */}
                {(scrapTableType === 'minister' || scrapTableType === 'overseas') && (
                  <div
                    className="btn alert"
                    onClick={() => {
                      if (!isLogin) {
                        alert('로그인이 필요합니다.');
                        return;
                      }
                      const draft = filtersToAlertDraft(scrapTableType, {
                        sort: hasSortTab ? selectedSort : [],
                        location: hasLocationTab ? selectedLocation : [],
                        position: hasPositionTab ? selectedPosition : [],
                        pay: hasPayTab ? selectedPay : [],
                      });
                      navigate(`/mypage/resumemanage?draft=${encodeURIComponent(JSON.stringify(draft))}`);
                    }}
                  >
                    <p>조건 저장 · 알림받기</p>
                  </div>
                )}
              </div>
            </div>
          </div>

          <div className="subpage__main__content">
            {isLoading ? (
              <div className="list-loading">
                <Loading />
              </div>
            ) : (
              <div className="main__content">
                <div className="recruit__wrap--category">
                  <div className="recruit__wrap--menu">
                    <div className="recruit_menu_box">
                      <p className="recruit_menu_text">교회 · 공고</p>
                    </div>
                    <div className="recruit_menu_box">
                      <p className="recruit_menu_text">지역 · 조건</p>
                    </div>
                    <div className="recruit_menu_box">
                      <p className="recruit_menu_text">등록 · 모집</p>
                    </div>
                  </div>

                  <div className="recruit__wrap--item">
                    {pagedList.length === 0 ? (
                      <div className="recruit__empty">검색 결과가 없습니다.</div>
                    ) : (
                      pagedList.map((item, index) => {
                        const part = safeJsonParse<PartEntry[]>(item.part, [
                          { sort: '전임', content: '' },
                        ]);
                        const pay = safeJsonParse<PayEntry[]>(item.pay, [
                          { sort: '전임', paySort: 'select', selectCost: '', inputCost: '' },
                        ]);
                        const applytime = safeJsonParse<ApplyTime>(item.applytime, {
                          startDay: '',
                          endDay: '',
                          daySort: '',
                        });
                        const scrapActive = scrapMap[scrapKeyOf({
                          targetType: 'recruit',
                          targetId: String(item.id),
                          tableType: scrapTableType,
                        })];
                        const location = config.tableType === 'overseas'
                          ? `${item.location || ''} ${item.locationDetail || ''}`.trim()
                          : `${item.location || ''}`;
                        const period = applytime.daySort !== ''
                          ? applytime.daySort
                          : applytime.endDay ? `~ ${applytime.endDay}` : '';

                        return (
                          <div
                            key={index}
                            className="recruit__item"
                            onClick={() => handleItemClick(item.id)}
                          >
                            {/* 1열: 직분 마커 · 교회 · 제목 */}
                            <div className="recruit__content">
                              <div className="recruit__name">
                                {config.showSortInListEtc && item.sort && (
                                  <span
                                    className={roleMarkerClass(item.sort)}
                                    dangerouslySetInnerHTML={{ __html: renderHighlighted(item.sort) }}
                                  />
                                )}
                                <p
                                  dangerouslySetInnerHTML={{
                                    __html: renderHighlighted(item.church),
                                  }}
                                />
                              </div>
                              <div className="recruit__title_box">
                                <p
                                  className="recruit__title"
                                  dangerouslySetInnerHTML={{
                                    __html: renderHighlighted(item.title),
                                  }}
                                />
                                <button
                                  type="button"
                                  className={`recruit__scrap${scrapActive ? ' recruit__scrap--on' : ''}`}
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    void handleToggleScrap(item);
                                  }}
                                  aria-label="스크랩">
                                  {scrapActive ? '♥' : '♡'}
                                </button>
                              </div>
                              <div className="recruit__sort_box">
                                {item.religiousbody && (
                                  <div className="recruit__religiousbody_box">
                                    <img
                                      src={`${MainURL}/siteimages/religiousbody/${item.religiousbody}.jpg`}
                                      alt=""
                                      onError={(e) => { (e.currentTarget as HTMLImageElement).style.display = 'none'; }}
                                    />
                                    <p
                                      className="recruit__religiousbody"
                                      dangerouslySetInnerHTML={{
                                        __html: renderHighlighted(item.religiousbody),
                                      }}
                                    />
                                  </div>
                                )}
                                {item.source && (
                                  <div className="recruit__religiousbody_box">
                                    {item.source !== '사역자모아' && (
                                      <img
                                        src={`${MainURL}/siteimages/schoolround/${item.source}.jpg`}
                                        alt=""
                                        onError={(e) => { (e.currentTarget as HTMLImageElement).style.display = 'none'; }}
                                      />
                                    )}
                                    <p
                                      className="recruit__religiousbody"
                                      dangerouslySetInnerHTML={{
                                        __html: renderHighlighted(item.source),
                                      }}
                                    />
                                  </div>
                                )}
                              </div>
                            </div>
                            {/* 2열: 지역 · 사역조건 · 사례비 */}
                            <div className="recruit__etc_box">
                              <div className="recruit__etc sortPay">
                                {location && (
                                  <p dangerouslySetInnerHTML={{ __html: renderHighlighted(location) }} />
                                )}
                                {item.part !== '' && item.part != null && part[0]?.content && (
                                  <p
                                    dangerouslySetInnerHTML={{
                                      __html: renderHighlighted(`사역조건 · ${renderPreview30(part[0].content)}`),
                                    }}
                                  />
                                )}
                                {config.showPayInList && (
                                  <>
                                    {renderPayLine(pay[0])}
                                    {pay.length > 1 && renderPayLine(pay[1])}
                                  </>
                                )}
                              </div>
                            </div>
                            {/* 3열: 등록일 · 모집기간 · 상세 */}
                            <div className="recruit__etc dateTime">
                              <p
                                className="recruit__date"
                                dangerouslySetInnerHTML={{
                                  __html: renderHighlighted(item.date),
                                }}
                              />
                              {period && (
                                <p
                                  className="recruit__period"
                                  dangerouslySetInnerHTML={{ __html: renderHighlighted(period) }}
                                />
                              )}
                              <span className="recruit__detail_btn">상세</span>
                            </div>
                          </div>
                        );
                      })
                    )}
                  </div>
                </div>
              </div>
            )}

            <Pagination
              currentPage={currentPage}
              totalPages={totalPages}
              onPageChange={(page) => {
                clearRecruitListRestore(config.listPath);
                setCurrentPage(page);
              }}
              onAfterChange={() => window.scrollTo(0, 500)}
            />
          </div>
        </div>
      </div>

      <ScrollToTopButton />
    </div>
  );
}
